/**
 * <s-floating-header> — homepage header that floats over the hero, then docks.
 *
 * Sets data-state="floating" while the page is at (or near) the top and the
 * enclosing sticky <header> has not yet stuck, and data-state="docked" once it
 * sticks to the top of the viewport. Styling keys off data-state via group
 * variants in the layout.
 *
 * External coupling: writes --floating-header-h (its live height) on the closest
 * <header>, whose negative bottom margin uses it so the header adds no page height
 * and the hero sits underneath it.
 */
class SFloatingHeader extends HTMLElement {
  #ac;
  #ro;
  #raf = 0;
  /** @type {HTMLElement | null} */
  #header = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#header = this.closest('header');

    this.#ro = new ResizeObserver(() => this.#measure());
    this.#ro.observe(this);
    this.#measure();

    window.addEventListener('scroll', () => this.#schedule(), { passive: true, signal: this.#ac.signal });
    window.addEventListener('resize', () => this.#schedule(), { passive: true, signal: this.#ac.signal });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ro?.disconnect();
    cancelAnimationFrame(this.#raf);
  }

  #measure() {
    this.#header?.style.setProperty('--floating-header-h', `${this.offsetHeight}px`);
  }

  #schedule() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.#update();
    });
  }

  #update() {
    const top = this.#header?.getBoundingClientRect().top ?? 0;
    const docked = window.scrollY > 8 && top <= 0.5;
    const state = docked ? 'docked' : 'floating';
    if (this.dataset.state !== state) this.dataset.state = state;
  }
}

customElements.define('s-floating-header', SFloatingHeader);
